import React, { useCallback, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Plus, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SchemaFormProps {
    schema: any;
    definitions?: any; // Root definitions for resolving $ref
    data: any;
    onChange: (data: any) => void;
    readOnly?: boolean;
    level?: number;
}

/**
 * A recursive component that renders a form based on a JSON Schema.
 * Supports: string, integer, number, boolean, enums, array, object, and $ref.
 */
export const SchemaForm: React.FC<SchemaFormProps> = ({
    schema: propSchema,
    definitions = {},
    data,
    onChange,
    readOnly = false,
    level = 0
}) => {

    // Resolve $ref if present at root of this level
    const schema = useMemo(() => {
        if (propSchema.$ref) {
            const refName = propSchema.$ref.split('/').pop();
            return definitions[refName] || propSchema;
        }
        return propSchema;
    }, [propSchema, definitions]);

    const handleChange = useCallback((key: string, value: any) => {
        onChange({ ...data, [key]: value });
    }, [data, onChange]);

    if (!schema) return null;

    // Handle Object (Properties)
    if (schema.properties) {
        return (
            <div className={`space-y-4 ${level > 0 ? 'p-3 bg-gray-50/50 rounded-lg border border-gray-100' : ''}`}>
                {Object.entries(schema.properties).map(([key, rawProp]: [string, any]) => {
                    // Resolve Property Level Ref
                    let prop = rawProp;
                    if (rawProp.$ref) {
                        const refName = rawProp.$ref.split('/').pop();
                        prop = definitions[refName] || rawProp;
                    }

                    const title = prop.title || key.replace(/([A-Z])/g, ' $1').trim(); // Fallback humanize
                    const value = data?.[key] ?? prop.default; // Use schema default if undefined
                    const description = prop.description;
                    const isRequired = schema.required?.includes(key);

                    // 1. Enum
                    if (prop.enum || (prop.type === 'string' && prop.enum)) {
                        return (
                            <div key={key} className="space-y-1.5">
                                <Label className="text-xs font-semibold text-gray-700">
                                    {title} {isRequired && <span className="text-red-500">*</span>}
                                </Label>
                                <Select
                                    value={value}
                                    onValueChange={(v) => handleChange(key, v)}
                                    disabled={readOnly}
                                >
                                    <SelectTrigger className="h-8 text-xs bg-white">
                                        <SelectValue placeholder="Select..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                        {(prop.enum || []).map((opt: string) => (
                                            <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {description && <p className="text-[10px] text-gray-500">{description}</p>}
                            </div>
                        );
                    }

                    // 2. Boolean
                    if (prop.type === 'boolean') {
                        return (
                            <div key={key} className="flex items-center justify-between space-x-2 py-1">
                                <div className="space-y-0.5">
                                    <Label className="text-xs font-semibold text-gray-700">{title}</Label>
                                    {description && <p className="text-[10px] text-gray-500">{description}</p>}
                                </div>
                                <Switch
                                    checked={!!value}
                                    onCheckedChange={(v) => handleChange(key, v)}
                                    disabled={readOnly}
                                />
                            </div>
                        );
                    }

                    // 3. String / Number
                    if (prop.type === 'string' || prop.type === 'integer' || prop.type === 'number') {
                        return (
                            <div key={key} className="space-y-1.5">
                                <Label className="text-xs font-semibold text-gray-700">{title}</Label>
                                <Input
                                    type={prop.type === 'integer' || prop.type === 'number' ? 'number' : 'text'}
                                    value={value ?? ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '') handleChange(key, undefined); // Handle clear
                                        else if (prop.type === 'integer') handleChange(key, parseInt(val));
                                        else if (prop.type === 'number') handleChange(key, parseFloat(val));
                                        else handleChange(key, val);
                                    }}
                                    className="h-8 text-xs bg-white"
                                    disabled={readOnly}
                                />
                                {description && <p className="text-[10px] text-gray-500">{description}</p>}
                            </div>
                        );
                    }

                    // 4. Object (Sub-Schema)
                    if (prop.type === 'object' || prop.properties) {
                        return (
                            <div key={key} className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700">{title}</Label>
                                <SchemaForm
                                    schema={prop}
                                    definitions={definitions}
                                    data={value || {}}
                                    onChange={(v) => handleChange(key, v)}
                                    level={level + 1}
                                />
                            </div>
                        );
                    }

                    // 5. Array
                    if (prop.type === 'array') {
                        const items = Array.isArray(value) ? value : [];
                        // Resolve Item Schema
                        let itemSchema = prop.items;
                        if (itemSchema && itemSchema.$ref) {
                            const refName = itemSchema.$ref.split('/').pop();
                            itemSchema = definitions[refName] || itemSchema;
                        }

                        return (
                            <div key={key} className="space-y-2">
                                <div className="flex justify-between items-center bg-gray-100 p-1 rounded px-2">
                                    <Label className="text-xs font-semibold text-gray-700">{title}</Label>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleChange(key, [...items, {}])}
                                        className="h-6 text-[10px] text-blue-600 hover:text-blue-800 hover:bg-white px-2"
                                        disabled={readOnly}
                                    >
                                        <Plus className="h-3 w-3 mr-1" /> Add
                                    </Button>
                                </div>

                                <div className="space-y-2">
                                    {items.map((item: any, idx: number) => (
                                        <div key={idx} className="relative group flex gap-2 items-start">
                                            <div className="flex-1">
                                                {/* Recursive Item Render */}
                                                {itemSchema.type === 'object' || itemSchema.properties ? (
                                                    <SchemaForm
                                                        schema={itemSchema}
                                                        definitions={definitions}
                                                        data={item}
                                                        onChange={(newItem) => {
                                                            const newItems = [...items];
                                                            newItems[idx] = newItem;
                                                            handleChange(key, newItems);
                                                        }}
                                                        level={level + 1}
                                                    />
                                                ) : (
                                                    // Primitive Array Item
                                                    <Input
                                                        value={item}
                                                        onChange={(e) => {
                                                            const newItems = [...items];
                                                            newItems[idx] = e.target.value;
                                                            handleChange(key, newItems);
                                                        }}
                                                        className="h-8 text-xs bg-white"
                                                    />
                                                )}
                                            </div>
                                            <button
                                                className="pt-2 text-gray-400 hover:text-red-500"
                                                onClick={() => {
                                                    const newItems = [...items];
                                                    newItems.splice(idx, 1);
                                                    handleChange(key, newItems);
                                                }}
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {items.length === 0 && <div className="text-[10px] text-gray-400 italic p-2 text-center">No items added.</div>}
                                </div>
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
        );
    }

    return null;
};
